import { z } from 'zod';

import { UuidSchema } from '../../common/id/id';

/**
 * A drag-and-drop reorder, submitted as **the whole new order** rather than a
 * sequence of moves.
 *
 * One request, one transaction, one final state: sending "move A after B" from
 * a builder where two staff are editing at once produces an order neither of
 * them arranged, and a per-row `PATCH sortOrder` produces a form that renders
 * mid-shuffle to any member who loads it between requests.
 *
 * The server assigns the actual `sortOrder` values from the array's positions,
 * so gaps and collisions are impossible by construction — a client never sends
 * an index it computed.
 *
 * Its limit is stated in `MoveQuestionSchema`, which arrived with paging: this
 * shape is only available to a client that holds the **whole** set. Sections and
 * options do; a paged, filtered question pane does not, and asking one to send an
 * order it cannot see is how a hundred-row ceiling gets defended as a feature.
 * Both endpoints stand, and each is used where its precondition holds.
 */
export const ReorderItemsSchema = z
  .object({
    /** Every id of the set being reordered, in the order they should appear. */
    ids: z.array(UuidSchema).min(1).max(500),
  })
  .strict()
  .refine((body) => new Set(body.ids).size === body.ids.length, {
    message: 'The same id appears more than once.',
    path: ['ids'],
  });

export type ReorderItemsRequest = z.infer<typeof ReorderItemsSchema>;
